import React from 'react';
import { Link } from 'react-router-dom';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';

const C = { void:'#030A3C', void2:'#060D4A', void3:'#0A1260', cyan:'#00FFFF', cyanDim:'rgba(0,255,255,0.10)', cyanBorder:'rgba(0,255,255,0.15)', magma:'#FF5722', magmaDim:'rgba(255,87,34,0.12)', white:'#F8F9FA', muted:'rgba(248,249,250,0.72)', muted2:'rgba(248,249,250,0.72)', border:'rgba(0,255,255,0.08)', border2:'rgba(0,255,255,0.15)', green:'#2A8B7A', greenDim:'rgba(42,139,122,0.12)' };

const PAINS = [
  { before:'Hours of recordings. Days of synthesis.', after:'Upload and get themes, findings, and quotes in minutes - not days.' },
  { before:'Affinity maps that live and die in FigJam.', after:'Research memory that\'s queryable six months later, from Claude.' },
  { before:'"Trust me, a user said this once."', after:'Every finding cites a verbatim quote with speaker and timestamp.' },
  { before:'Findings arrive after the product decision.', after:'Synthesis ready before the next sprint planning.' },
];

const PRINCIPLES = [
  {
    title: 'Quote fidelity over synthesis quality',
    body: 'Researcher credibility depends on accuracy. SenseCritiq never paraphrases or invents quotes. Every piece of evidence is a verbatim excerpt from the original recording - speaker-labelled, timestamped, session-linked.',
  },
  {
    title: 'No unsourced findings',
    body: 'A post-processing step validates that every finding cites at least one supporting quote before it\'s returned. If Claude Sonnet generates an unsupported claim, it\'s discarded - not surfaced.',
  },
  {
    title: 'Your data stays yours',
    body: 'Research files are never used to train models. Files are encrypted at rest and in transit. Access is restricted to your account. You can request deletion at any time.',
  },
  {
    title: 'AI aids the researcher, not replaces them',
    body: 'SenseCritiq surfaces the signal. The researcher decides what it means. Themes and findings are starting points for your analysis, not conclusions. You stay in the driver\'s seat.',
  },
];

const USECASES = [
  { role:'Solo UX researcher', desc:'Run 10 sessions a month on the Starter plan. Get synthesis ready for weekly design reviews. Stop spending your Sunday on affinity maps.' },
  { role:'ResearchOps lead', desc:'Centralise your organisation\'s research memory. Every interview, every survey, every usability test - queryable from Claude. Onboard new researchers to existing knowledge in minutes.' },
  { role:'Product team with embedded research', desc:'PM and designer run quick interviews. Upload straight from Claude Desktop. Get structured findings before the next retro. No research background required.' },
  { role:'UX research agency', desc:'Process client sessions under separate projects. Generate branded PDF reports. Team plan covers unlimited sessions across all engagements.' },
];

export default function Researchers() {
  return (
    <div style={s.root}>
      <MarketingNav />

      {/* Hero */}
      <div style={s.hero} className="scq-hero">
        <div style={s.heroBg} />
        <div style={s.eyebrow}>For UX researchers</div>
        <h1 style={s.h1}>
          Spend your time on insight,<br/>
          <span style={s.h1Accent}>not synthesis.</span>
        </h1>
        <p style={s.heroSub}>
          SenseCritiq is built to the standards researchers need - source-cited findings,
          verbatim quotes, speaker diarisation. AI that augments your expertise, not replaces it.
        </p>
        <div style={s.heroCtas}>
          <Link to="/sign-up" style={s.ctaPrimary}>Start for free</Link>
          <Link to="/how-it-works" style={s.ctaGhost}>See the pipeline →</Link>
        </div>
      </div>

      {/* Before / after */}
      <div style={s.baWrap} className="scq-section">
        <div style={s.baInner}>
          <div style={s.sectionEyebrow}>Before and after</div>
          <h2 style={s.sectionH2}>What changes when synthesis is instant.</h2>
          <div style={s.baTable}>
            <div style={s.baTableHead}>
              <div style={s.baHeadBefore}><span style={s.baColIcon}>✗</span> Without SenseCritiq</div>
              <div style={s.baHeadAfter}><span style={s.baColIconGreen}>✓</span> With SenseCritiq</div>
            </div>
            {PAINS.map((p, i) => (
              <div key={i} style={{...s.baRow, ...(i === PAINS.length - 1 ? {borderBottom:'none'} : {})}}>
                <div style={s.baCellBefore}>
                  <div style={s.baCellLabel}><span style={s.baColIcon}>✗</span> Before</div>
                  {p.before}
                </div>
                <div style={s.baCellAfter}>
                  <div style={s.baCellLabel}><span style={s.baColIconGreen}>✓</span> After</div>
                  {p.after}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Principles */}
      <div style={s.principlesWrap}>
        <div style={s.principlesInner}>
          <div style={s.sectionEyebrow}>Research trust</div>
          <h2 style={s.sectionH2}>Built around how researchers think.</h2>
          <div style={s.principlesGrid} className="scq-grid-2">
            {PRINCIPLES.map(p => (
              <div key={p.title} className="scq-card" style={s.principleCard}>
                <h3 style={s.principleTitle}>{p.title}</h3>
                <p style={s.principleBody}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quote example */}
      <div style={s.quoteWrap}>
        <div style={s.quoteInner}>
          <div style={s.sectionEyebrow}>Example output</div>
          <h2 style={s.sectionH2}>Findings you can actually use.</h2>
          <div className="scq-card" style={s.quoteExample}>
            <div style={s.findingBlock}>
              <div style={s.findingLabel}>Finding</div>
              <div style={s.findingStatement}>The invite flow breaks when the invitee already has an account, blocking successful team setup during onboarding.</div>
            </div>
            <div style={s.evidenceBlock}>
              <div style={s.evidenceLabel}>Supporting evidence</div>
              <div className="scq-card" style={s.quoteCard}>
                <div style={s.quoteText}>"I kept hitting that wall every time I tried to invite someone. I just gave up and sent them a Loom instead."</div>
                <div style={s.quoteMeta}>
                  <span style={s.quoteMetaItem}>Speaker B</span>
                  <span style={s.quoteMetaDot}>·</span>
                  <span style={s.quoteMetaItem}>2:22</span>
                  <span style={s.quoteMetaDot}>·</span>
                  <span style={s.quoteMetaItem}>Onboarding exit interviews - March</span>
                </div>
              </div>
              <div className="scq-card" style={s.quoteCard}>
                <div style={s.quoteText}>"My co-founder already had an account from a trial. The system just didn't know what to do."</div>
                <div style={s.quoteMeta}>
                  <span style={s.quoteMetaItem}>Speaker A</span>
                  <span style={s.quoteMetaDot}>·</span>
                  <span style={s.quoteMetaItem}>14:05</span>
                  <span style={s.quoteMetaDot}>·</span>
                  <span style={s.quoteMetaItem}>Onboarding exit interviews - March</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Use cases */}
      <div style={s.usecasesWrap}>
        <div style={s.usecasesInner}>
          <div style={s.sectionEyebrow}>Who it's for</div>
          <h2 style={s.sectionH2}>Researchers at every scale.</h2>
          <div style={s.usecasesGrid} className="scq-grid-2">
            {USECASES.map(u => (
              <div key={u.role} className="scq-card" style={s.usecaseCard}>
                <div style={s.usecaseRole}>{u.role}</div>
                <p style={s.usecaseDesc}>{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div style={s.bottomCta}>
        <div style={s.bottomCtaInner} className="scq-bottom-inner">
          <div style={s.bottomGlow} />
          <h2 style={s.bottomH2}>Your next session is two minutes away.</h2>
          <p style={s.bottomSub}>Free plan includes 2 sessions/month. No credit card. No commitment.</p>
          <div style={s.bottomBtns}>
            <Link to="/sign-up" style={s.ctaPrimary}>Create free account</Link>
            <Link to="/pricing" style={s.ctaGhost}>View pricing →</Link>
          </div>
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
}

const s = {
  root: { background:C.void, minHeight:'100vh', fontFamily:"'Inter',sans-serif", color:C.white },

  hero: { textAlign:'center', padding:'100px 24px 90px', position:'relative', overflow:'hidden' },
  heroBg: { position:'absolute', inset:0, background:'radial-gradient(ellipse 70% 55% at 50% 0%, rgba(0,255,255,0.08) 0%, transparent 60%)', pointerEvents:'none' },
  eyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16, position:'relative' },
  h1: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(38px,5.5vw,70px)', fontWeight:700, letterSpacing:'-0.04em', lineHeight:1.05, marginBottom:24, position:'relative' },
  h1Accent: { color:C.cyan },
  heroSub: { fontSize:'clamp(16px,1.8vw,19px)', color:C.muted, maxWidth:560, margin:'0 auto 40px', lineHeight:1.7, position:'relative' },
  heroCtas: { display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap', position:'relative' },
  ctaPrimary: { display:'inline-block', padding:'14px 30px', borderRadius:10, background:C.magma, color:'#fff', fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:15, textDecoration:'none' },
  ctaGhost: { display:'inline-block', padding:'14px 26px', borderRadius:10, background:'rgba(255,255,255,0.05)', color:C.muted, fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:600, fontSize:15, textDecoration:'none', border:`1px solid ${C.border}` },

  baWrap: { background:C.void2, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, padding:'80px 24px' },
  baInner: { maxWidth:900, margin:'0 auto' },
  sectionEyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16, textAlign:'center' },
  sectionH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(26px,3.5vw,44px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.1, marginBottom:48, textAlign:'center' },
  baTable: { border:`1px solid ${C.border}`, borderRadius:16, overflow:'hidden' },
  baTableHead: { display:'flex', flexWrap:'wrap', background:C.void3, borderBottom:`1px solid ${C.border}` },
  baHeadBefore: { flex:'1 1 200px', padding:'14px 20px', fontSize:11, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:C.muted2, display:'flex', alignItems:'center', gap:8, borderRight:`1px solid ${C.border}` },
  baHeadAfter: { flex:'1 1 200px', padding:'14px 20px', fontSize:11, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:C.muted2, display:'flex', alignItems:'center', gap:8 },
  baColIcon: { color:'rgba(255,87,34,0.7)', fontSize:14 },
  baColIconGreen: { color:C.green, fontSize:14 },
  baRow: { display:'flex', flexWrap:'wrap', borderBottom:`1px solid ${C.border}` },
  baCellBefore: { flex:'1 1 200px', minWidth:0, padding:'20px', fontSize:15, color:'rgba(248,249,250,0.62)', lineHeight:1.65, borderRight:`1px solid ${C.border}` },
  baCellAfter: { flex:'1 1 200px', minWidth:0, padding:'20px', fontSize:15, color:C.muted, lineHeight:1.65 },
  baCellLabel: { fontSize:10, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:C.muted2, marginBottom:8, display:'flex', alignItems:'center', gap:6 },

  principlesWrap: { padding:'80px 24px' },
  principlesInner: { maxWidth:1100, margin:'0 auto' },
  principlesGrid: { display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:20 },
  principleCard: { background:'linear-gradient(135deg, rgba(6,13,74,0.65) 0%, rgba(10,18,96,0.72) 100%)', border:`1px solid ${C.border}`, borderRadius:18, padding:'32px 28px', boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  principleTitle: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:19, fontWeight:700, letterSpacing:'-0.02em', marginBottom:12, color:C.white },
  principleBody: { fontSize:14, color:C.muted, lineHeight:1.75 },

  quoteWrap: { background:C.void2, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, padding:'80px 24px' },
  quoteInner: { maxWidth:800, margin:'0 auto' },
  quoteExample: { background:'linear-gradient(135deg, rgba(10,18,96,0.70) 0%, rgba(14,22,110,0.75) 100%)', border:`1px solid ${C.border2}`, borderRadius:20, padding:'32px 36px', boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  findingBlock: { marginBottom:28 },
  findingLabel: { fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:C.cyan, marginBottom:10 },
  findingStatement: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:18, fontWeight:700, letterSpacing:'-0.02em', lineHeight:1.4 },
  evidenceBlock: {},
  evidenceLabel: { fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:C.muted2, marginBottom:12 },
  quoteCard: { background:'linear-gradient(135deg, rgba(6,13,74,0.65) 0%, rgba(10,18,96,0.72) 100%)', border:`1px solid ${C.border}`, borderRadius:12, padding:'18px 20px', marginBottom:10, boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  quoteText: { fontSize:15, color:C.muted, lineHeight:1.7, fontStyle:'italic', marginBottom:10 },
  quoteMeta: { display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' },
  quoteMetaItem: { fontSize:11, color:C.muted2 },
  quoteMetaDot: { fontSize:11, color:C.muted2 },

  usecasesWrap: { padding:'80px 24px' },
  usecasesInner: { maxWidth:1100, margin:'0 auto' },
  usecasesGrid: { display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:16 },
  usecaseCard: { background:'linear-gradient(135deg, rgba(6,13,74,0.65) 0%, rgba(10,18,96,0.72) 100%)', border:`1px solid ${C.border}`, borderRadius:16, padding:'28px 24px', boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  usecaseRole: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:16, fontWeight:700, color:C.cyan, marginBottom:10 },
  usecaseDesc: { fontSize:14, color:C.muted, lineHeight:1.7 },

  bottomCta: { padding:'80px 24px 100px' },
  bottomCtaInner: { maxWidth:1052, margin:'0 auto', background:`linear-gradient(135deg, ${C.void3} 0%, #0A0E4A 100%)`, border:`1px solid ${C.border2}`, borderRadius:24, padding:'80px 48px', textAlign:'center', position:'relative', overflow:'hidden' },
  bottomGlow: { position:'absolute', top:'-40%', left:'50%', transform:'translateX(-50%)', width:500, height:400, borderRadius:'50%', background:'radial-gradient(circle, rgba(0,255,255,0.06) 0%, transparent 70%)', pointerEvents:'none' },
  bottomH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(26px,3.5vw,44px)', fontWeight:700, letterSpacing:'-0.03em', marginBottom:16, position:'relative' },
  bottomSub: { fontSize:17, color:C.muted, marginBottom:36, position:'relative' },
  bottomBtns: { display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap', position:'relative' },
};
